import axios from "axios";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../Router/all_routes";

const LIVE_API_URL = "https://tmis.co.za/TMIS_Portal/Portal_Api/api";

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

const api = axios.create({
  baseURL: LIVE_API_URL,
  headers: { "Content-Type": "application/json" },
});

// Attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("pos_access_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-refresh on 401
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config as Record<string, unknown>;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            (originalRequest as Record<string, unknown>).headers = {
              ...(originalRequest.headers as Record<string, unknown>),
              Authorization: `Bearer ${newToken}`
            };
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            resolve(api(originalRequest as any));
          });
        });
      }

      isRefreshing = true;
      const refreshToken = localStorage.getItem("pos_refresh_token");

      if (!refreshToken) {

        const navigate = useNavigate();
        localStorage.clear();
        navigate(all_routes.signin);
        return Promise.reject(error);
      }

      try {
        const res = await axios.post(`${LIVE_API_URL}/auth/refresh`, {
          RefreshToken: refreshToken,
        });

        const data = res.data as {
          Success: boolean;
          Data?: {
            AccessToken?: string;
            RefreshToken?: string;
          };
        };

        if (data.Success && data.Data?.AccessToken) {
          const newToken = data.Data.AccessToken;
          const newRefresh = data.Data.RefreshToken || refreshToken;

          localStorage.setItem("pos_access_token", newToken);
          localStorage.setItem("pos_refresh_token", newRefresh);

          api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          onTokenRefreshed(newToken);

          (originalRequest as Record<string, unknown>).headers = {
            ...(originalRequest.headers as Record<string, unknown>),
            Authorization: `Bearer ${newToken}`
          };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return api(originalRequest as any);
        } else {
          localStorage.clear();
          const navigate = useNavigate();
          navigate(all_routes.signin);
          return Promise.reject(error);
        }
      } catch (refreshErr) {
        console.error("Token refresh failed:", refreshErr);
        localStorage.clear();
        const navigate = useNavigate();
          navigate(all_routes.signin);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
